import {api} from '@nfq/typed-next-api';

import type {HTTP_METHODS, MutationRepoArgs} from '@nfq/typed-next-api';
import type {saveContact} from 'ApiRoutes/contact';
import type {ContactFormServiceAdapter} from 'Domain/adapters/ContactFormServiceAdapter';
import type {ContactFormData} from 'Domain/entities/ContactForm';

/**
 * The `ContactFormService` class provides functionality to send contact form data to the backend API.
 * This service implements the `ContactFormServiceAdapter` interface and handles the communication
 * between the frontend contact forms and the server-side opt-in endpoint for processing contact submissions.
 */
class ContactFormService implements ContactFormServiceAdapter {
    /**
     * Sends contact form data to the backend API endpoint for processing and storage.
     * This method handles the HTTP POST request to submit user contact information through
     * the opt-in API route, ensuring proper data transmission and API communication.
     *
     * @param key              The unique identifier key for the contact form submission.
     * @param props            The mutation repository arguments containing request data.
     * @param props.arg        The argument object containing the request body and HTTP method.
     * @param props.arg.body   The contact form data to be submitted to the server.
     * @param props.arg.method The HTTP method to use for the API request (POST).
     * @returns A promise that resolves when the contact form has been successfully submitted.
     *
     * @example
     * ```tsx
     * const contactService = new ContactFormService();
     * await contactService.sendContactForm('contact-form-1', {
     *     arg: {
     *         body: {
     *             name: 'John Doe',
     *             email: 'john@example.com',
     *             message: 'Hello, I am interested in your services.'
     *         },
     *         method: 'POST'
     *     }
     * });
     * ```
     */
    async sendContactForm(key: string, {arg: {body, method}}: MutationRepoArgs<ContactFormData, HTTP_METHODS.POST>) {
        await api<typeof saveContact>('/api/contact', {
            body,
            method
        });
    }
}

export const contactFormService = new ContactFormService();